<script setup>
/**
 * A source that is still being read, said in words rather than as a bar.
 *
 * THERE IS NO PERCENTAGE, AND THAT IS DELIBERATE. Nothing upstream knows how many pages a
 * site has before it has crawled them, or how many answers a document will yield — so a
 * progress bar here would be an animation pretending to be a measurement, and the first
 * time it sat at 80% for two minutes it would have taught the owner not to believe it.
 * What IS known is the phase and the count so far ("reading your website · pages read so
 * far: 12"), which is a real fact that moves, and a spinner, which is honest about being
 * a spinner.
 *
 * FOUR STATES, EACH WITH SOMETHING TO DO OR A REASON THERE IS NOTHING:
 *   • waiting / reading   — nothing to do; the sentence says so ("you can close this
 *                            page"), because the commonest support question about a slow
 *                            crawl is "do I have to sit here?".
 *   • ready               — the one moment this card exists for. It stays on screen after
 *                            the job finishes, with a link into what it produced, so the
 *                            owner who pressed Add gets told it worked instead of watching
 *                            a card silently vanish.
 *   • could not be read   — one plain sentence from the job's reason CODE (P-5.6), never
 *                            the ingester's own text, which named vendors and status
 *                            numbers. The sentence says what to try, because that is the
 *                            difference between fixing the source and giving up on it.
 *                            It can be added again or DISMISSED — without the second, a
 *                            failure sat in this list and the Overview queue for the
 *                            whole 180-day job retention, long after the owner had
 *                            given up on it or added it again successfully.
 *   • stopped watching    — after the poll ladder's hard stop. Not a failure: the work
 *                            carries on in the background, and this says so and offers to
 *                            look again. A frozen spinner would have said the opposite.
 *
 * Colour comes from the state and only from the state, and it agrees with the Overview's
 * attention queue on purpose: a source that failed is AMBER on both screens
 * (`ATTENTION_LEVEL.GAP`), because it is a gap in what the assistant knows, not something
 * that has stopped the product.
 */
import { computed } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';
import { receiptPhrase } from '../helpers/ai-price.js';

const props = defineProps({
  /** A row from `wasloApi.getJobs()`. */
  job: { type: Object, required: true },
  accountId: { type: Number, default: null },
  /** The poll ladder reached its hard stop while this job was still running. */
  isStalled: { type: Boolean, default: false },
  /** A dismiss of this (failed) job is in flight. */
  isDismissing: { type: Boolean, default: false },
  /** Print what the job charged (P-5.12): only for a job the owner just added. */
  showReceipt: { type: Boolean, default: false },
});

const emit = defineEmits(['again', 'check', 'dismiss']);

const { t, locale } = useWasloI18n();

const formatCount = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

const isRunning = computed(
  () => props.job.status === 'queued' || props.job.status === 'running'
);
const isStopped = computed(() => isRunning.value && props.isStalled);

// What the finished job charged, from the server's `charged` (0 for a skip or a failure).
const receipt = computed(() =>
  props.showReceipt &&
  (props.job.status === 'succeeded' || props.job.status === 'failed')
    ? receiptPhrase(t, locale.value, props.job.charged)
    : ''
);

/**
 * P-6.7: what was read from photos or a scanned PDF, on a finished job only. Counts are
 * plural choices (six forms in Arabic); the lines it could not read are her own words, shown
 * as they are so she can add them by hand.
 */
const reading = computed(() => {
  const r = props.job.receipt;
  if (props.job.status !== 'succeeded' || !r) return null;
  const lines = [
    t('WASLO.RECEIPT.PAGES', { count: formatCount(r.pagesRead) }, r.pagesRead),
    t(
      'WASLO.RECEIPT.LEARNED',
      { count: formatCount(r.answersLearned) },
      r.answersLearned
    ),
  ];
  if (r.linesKept > 0) {
    lines.push(
      t('WASLO.RECEIPT.LINES', { count: formatCount(r.linesKept) }, r.linesKept)
    );
  }
  if (r.pagesUnreadable.length > 0) {
    lines.push(
      t('WASLO.RECEIPT.PAGES_UNREADABLE', {
        pages: r.pagesUnreadable.map(formatCount).join(', '),
      })
    );
  }
  return { summary: lines.join(' '), unreadable: r.linesUnreadable };
});

/**
 * One computed for the whole face of the card, so a state can never show one status's
 * words next to another status's colour — the two used to be separate lookups and the
 * failed row inherited the running row's blue for exactly one frame on every poll.
 */
const face = computed(() => {
  const job = props.job;

  if (isStopped.value) {
    return {
      tone: 'wait',
      icon: 'i-lucide-clock',
      title: t('WASLO.ANSWERS.WORKING.STALLED'),
      body: t('WASLO.ANSWERS.WORKING.STALLED_BODY'),
    };
  }
  if (job.status === 'queued') {
    return {
      tone: 'busy',
      title: t('WASLO.ANSWERS.WORKING.QUEUED'),
      body: t('WASLO.ANSWERS.WORKING.QUEUED_BODY'),
    };
  }
  if (job.status === 'running') {
    if (job.phase === 'crawling') {
      return {
        tone: 'busy',
        title: t('WASLO.ANSWERS.WORKING.CRAWLING'),
        body: t('WASLO.ANSWERS.WORKING.CRAWLING_BODY', {
          count: formatCount(job.pagesCrawled),
        }),
      };
    }
    if (job.phase === 'generating') {
      return {
        tone: 'busy',
        title: t('WASLO.ANSWERS.WORKING.GENERATING'),
        body: t('WASLO.ANSWERS.WORKING.GENERATING_BODY', {
          count: formatCount(job.faqsGenerated),
        }),
      };
    }
    return {
      tone: 'busy',
      title: t('WASLO.ANSWERS.WORKING.RUNNING'),
      body: t('WASLO.ANSWERS.WORKING.RUNNING_BODY'),
    };
  }
  if (job.status === 'succeeded') {
    return {
      tone: 'done',
      icon: 'i-lucide-check',
      title: t('WASLO.ANSWERS.WORKING.READY'),
      // A job that succeeded and generated nothing is the quiet failure this section
      // exists to stop: it is "done" everywhere upstream and useless to the assistant.
      body:
        job.faqsGenerated > 0
          ? t('WASLO.ANSWERS.WORKING.READY_BODY')
          : t('WASLO.ANSWERS.WORKING.READY_EMPTY'),
    };
  }
  return {
    tone: 'gap',
    icon: 'i-lucide-file-warning',
    title: t('WASLO.ANSWERS.WORKING.FAILED'),
    // P-5.6: one plain sentence from the reason CODE; the generic one when there is none.
    // The ingester's own text is never shown: it named vendors and status numbers.
    body: props.job.reason
      ? t(`WASLO.ANSWERS.REASON.${props.job.reason.toUpperCase()}`)
      : t('WASLO.ANSWERS.WORKING.FAILED_BODY'),
  };
});

const TONES = {
  busy: { tile: 'bg-n-blue-3', mark: 'text-n-blue-10' },
  done: { tile: 'bg-n-blue-3', mark: 'text-n-blue-10' },
  gap: { tile: 'bg-n-amber-3', mark: 'text-n-amber-11' },
  wait: { tile: 'bg-n-amber-3', mark: 'text-n-amber-11' },
};

const tone = computed(() => TONES[face.value.tone]);

/**
 * Succeeded, with something worth knowing (P-5.6), said from the note CODE. "Nothing found"
 * on a source that taught nothing is already the card's body, so it is not said twice.
 */
const note = computed(() => {
  const code = props.job.note;
  if (props.job.status !== 'succeeded' || !code) return '';
  if (code === 'nothing_found' && props.job.faqsGenerated === 0) return '';
  return t(`WASLO.ANSWERS.NOTE.${code.toUpperCase()}`);
});

const openTo = computed(() =>
  props.job.status === 'succeeded' &&
  props.job.documentId !== null &&
  props.accountId !== null
    ? frontendURL(
        `accounts/${props.accountId}/waslo/answers/${props.job.documentId}`
      )
    : null
);
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
    :data-waslo-job="job.id"
    :data-waslo-job-status="job.status"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9"
      :class="tone.tile"
      aria-hidden="true"
    >
      <!-- A spinner while it is genuinely moving; a mark the moment it is not. -->
      <Spinner v-if="!face.icon" :size="16" :class="tone.mark" />
      <span v-else class="size-4" :class="[face.icon, tone.mark]" />
    </span>

    <div class="flex flex-col min-w-0 gap-1">
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-job-title
      >
        <bdi>{{ job.title }}</bdi>
      </p>
      <!-- The state and its sentence on two lines, so no dash is needed between them. -->
      <p
        class="mb-0 text-sm break-words text-n-slate-12"
        dir="auto"
        data-waslo-job-state
      >
        {{ face.title }}
      </p>
      <p
        class="mb-0 text-sm break-words text-n-slate-11"
        dir="auto"
        data-waslo-job-body
      >
        {{ face.body }}
      </p>
      <p
        v-if="note"
        class="mb-0 text-sm break-words text-n-slate-11"
        dir="auto"
      >
        {{ note }}
      </p>
      <p
        v-if="receipt"
        class="mb-0 text-xs text-n-slate-11"
        data-waslo-job-receipt
      >
        {{ receipt }}
      </p>
      <div
        v-if="reading"
        class="flex flex-col gap-1 text-sm text-n-slate-11"
        data-waslo-job-reading
      >
        <p class="mb-0">{{ reading.summary }}</p>
        <template v-if="reading.unreadable.length">
          <p class="mb-0">{{ t('WASLO.RECEIPT.LINES_UNREADABLE') }}</p>
          <ul class="mb-0 ps-4 list-disc">
            <li
              v-for="(line, index) in reading.unreadable"
              :key="index"
              dir="auto"
            >
              <bdi>{{ line }}</bdi>
            </li>
          </ul>
        </template>
      </div>
    </div>

    <!-- The same slot, in the same order, on every row. -->
    <div class="flex items-center gap-3 ms-0 shrink-0 sm:ms-auto">
      <router-link
        v-if="openTo"
        :to="openTo"
        class="inline-flex items-center h-8 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
        data-waslo-job-open
      >
        {{ t('WASLO.ANSWERS.WORKING.OPEN') }}
      </router-link>
      <Button
        v-else-if="isStopped"
        :label="t('WASLO.ANSWERS.WORKING.CHECK')"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-job-check
        @click="emit('check')"
      />
      <template v-else-if="job.status === 'failed'">
        <Button
          :label="t('WASLO.ANSWERS.WORKING.AGAIN')"
          variant="faded"
          color="slate"
          size="sm"
          data-waslo-job-again
          @click="emit('again', job)"
        />
        <Button
          :label="t('WASLO.ANSWERS.WORKING.DISMISS')"
          :aria-label="
            t('WASLO.ANSWERS.WORKING.DISMISS_LABEL', { title: job.title })
          "
          :is-loading="isDismissing"
          :disabled="isDismissing"
          variant="ghost"
          color="slate"
          size="sm"
          data-waslo-job-dismiss
          @click="emit('dismiss', job)"
        />
      </template>
    </div>
  </li>
</template>
